// src/auth/rpWorkspaceTransition.ts
//
// The single, canonical RP/BFF workspace-transition primitive.
//
// In RP/BFF mode the browser holds no token, so a workspace change cannot be a
// data mutation — it must re-mint the server-side `crm_sid` session bound to the
// target workspace. That is exactly what the existing `/auth/start` flow does:
//
//   /auth/start?returnTo=<path>&workspaceId=<id>
//     → destroys the stale `crm_sid` when its bound workspace differs (PR #64),
//     → redirects to Central `/api/oidc/authorize?workspaceId` (PR #69 + Central
//       PR #194) which binds the auth code to the requested workspace,
//     → `/auth/callback` mints a NEW `crm_sid` for that workspace,
//     → the full-page navigation re-boots the app; `RpBootGate` re-runs
//       `GET /auth/context` and hydrates a fresh `bootContext` + entitlements.
//
// This helper is the ONE implementation of that transition. It was previously
// inlined inside `shell/SuiteShellAdapter.tsx`'s `rpWorkspaceSwitchHandler`
// (the Suite Shell workspace switcher); it is extracted here — dependency-free —
// so the Workspaces page (create + switch) can reuse it WITHOUT importing the
// heavy shell module and WITHOUT duplicating the URL/navigation logic.
//
// No browser token is introduced. Malformed input is deliberately NOT rejected
// here — the server's UUID-shape validator (PR #64) is the canonical guard; we
// only encode for safety.

/**
 * Begin the canonical RP/BFF transition into `workspaceId` via the server
 * `/auth/start` re-mint flow. This performs a full-page navigation, so control
 * does not return to the caller in practice.
 *
 * @param workspaceId target workspace to bind the new `crm_sid` session to
 * @param returnTo    path to land on after the callback completes (default `/`)
 */
export function beginRpWorkspaceTransition(workspaceId: string, returnTo = '/'): void {
  const target =
    '/auth/start?returnTo=' +
    encodeURIComponent(returnTo) +
    '&workspaceId=' +
    encodeURIComponent(workspaceId);
  window.location.assign(target);
}
